"use client";

import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../lib/utils";
import { ICON_PRESET_CLASSES, IconComponent } from "./icon";
import { Spinner } from "./spinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium !no-underline transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",

  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-transparent text-foreground shadow-sm hover:bg-ui-accent hover:text-ui-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost:
          "hover:text-accent-foreground text-foreground hover:bg-ui-accent",
        link: "text-primary underline-offset-4 hover:underline",
        transparent: "",
      },
      size: {
        xs: `h-6 min-w-[3.625rem] gap-1 px-2 text-xs`,
        sm: `h-8 min-w-[4.625rem] px-3 text-xs`,
        default: `h-9 min-w-[5rem] px-4 py-2`,
        lg: `h-10 px-8 py-3`,

        content: "",
        smIcon: `size-8`,
        icon: `size-9`,
        iconCircle: `size-9 rounded-full`,
        smIconCircle: `size-8 rounded-full`,
      },
      iconSize: {
        xs: ICON_PRESET_CLASSES.sizeForSVG.xs,
        sm: ICON_PRESET_CLASSES.sizeForSVG.sm,
        default: ICON_PRESET_CLASSES.sizeForSVG.default,
        lg: ICON_PRESET_CLASSES.sizeForSVG.lg,
        xl: ICON_PRESET_CLASSES.sizeForSVG.xl,
        "2xl": ICON_PRESET_CLASSES.sizeForSVG["2xl"],
        "3xl": ICON_PRESET_CLASSES.sizeForSVG["3xl"],

        content: ICON_PRESET_CLASSES.sizeForSVG.default,
        smIcon: ICON_PRESET_CLASSES.sizeForSVG.sm,
        icon: ICON_PRESET_CLASSES.sizeForSVG.default,
        iconCircle: ICON_PRESET_CLASSES.sizeForSVG.default,
        smIconCircle: ICON_PRESET_CLASSES.sizeForSVG.sm,

        inherit: "",
      },
      disabled: {
        true: "pointer-events-none opacity-50",
      },
      overrideClass: {
        true: "ui-button",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      iconSize: "default",
    },
  },
);

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
    /**
     * @deprecated Use `sectionLeft` or `sectionRight` instead.
     */
    icon?: IconComponent;
    sectionLeft?: React.ReactNode;
    sectionRight?: React.ReactNode;
  };
/**
 * Radix UI (API reference): https://www.radix-ui.com/primitives/docs/utilities/slot
 *
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/button
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size,
      iconSize,
      overrideClass = true,
      className,

      asChild = false,
      children,
      icon,
      loading,
      sectionLeft,
      sectionRight,
      type = "button",

      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        aria-busy={loading}
        className={cn(
          buttonVariants({
            variant,
            size,
            iconSize: iconSize ?? size,
            overrideClass,
            className,
          }),
        )}
        ref={ref}
        type={type}
        {...props}
      >
        {loading && !sectionLeft && !sectionRight ? <Spinner /> : icon}

        {sectionLeft && loading ? <Spinner /> : sectionLeft}

        <Slottable>{children}</Slottable>

        {sectionRight && loading ? <Spinner /> : sectionRight}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants, type ButtonProps };
